
import type { BuiltPowerPlan, PowerPlanSettings } from '@/lib/power-plan'
import { cx, card } from '@/lib/ui'

type Rung = { key: string; label: string; detail: string; done: boolean; partial?: boolean }

/**
 * Four rungs, each a real deliverable, and exactly one next action.
 *
 * A gaps list is a backlog and invites being closed; a ladder invites being
 * climbed. Ballpark answers "roughly how big a genset?"; Sized, "does it fit?";
 * Per-circuit, "how do I split it?"; Verified, "what did it actually pull?"
 */
export function PowerLadder({
  built,
  settings,
}: {
  built: BuiltPowerPlan
  settings: PowerPlanSettings
}) {
  const counted = built.rows.filter((row) => !row.zeroReason).length
  const grouped = built.rows.filter((row) => !row.zeroReason && row.groupIds.length > 0).length
  const measured = built.rows.filter((row) => row.provenance === 'MEASURED').length
  const supplySet = settings.supply.kind !== 'NONE'

  const rungs: Rung[] = [
    {
      key: 'ballpark',
      label: 'Ballpark',
      detail: counted === 0 ? 'no rows yet' : `${counted} ${counted === 1 ? 'row' : 'rows'}`,
      done: counted > 0,
    },
    {
      key: 'sized',
      label: 'Sized',
      detail: supplySet ? 'supply set' : 'no supply set',
      done: supplySet,
    },
    {
      key: 'circuits',
      label: 'Per-circuit',
      detail: counted === 0 ? '—' : `${grouped} of ${counted} on a circuit`,
      done: counted > 0 && grouped === counted,
      partial: grouped > 0 && grouped < counted,
    },
    {
      key: 'verified',
      label: 'Verified',
      detail: `${measured} measured`,
      done: counted > 0 && measured === counted,
      partial: measured > 0 && measured < counted,
    },
  ]

  const next = nextAction({ counted, grouped, supplySet, measured, built })

  return (
    <section className={cx(card, 'px-5 py-4')} aria-label="Plan progress">
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-2">
        {rungs.map((rung, index) => (
          <li key={rung.key} className="flex items-center gap-1">
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className={cx(
                  'grid h-3.5 w-3.5 place-items-center rounded-full text-[8px] text-on-ink',
                  rung.done
                    ? 'bg-[var(--ink)]'
                    : rung.partial
                      ? 'bg-[var(--ink)]/40'
                      : 'border border-[var(--border-strong)]',
                )}
              >
                {rung.done ? '✓' : ''}
              </span>
              <span className="text-[11px]">
                <span
                  className={cx(
                    'font-medium uppercase tracking-[0.08em]',
                    rung.done || rung.partial ? 'text-foreground' : 'text-muted',
                  )}
                >
                  {rung.label}
                </span>
                <span className="ml-1.5 text-muted">{rung.detail}</span>
              </span>
            </span>
            {index < rungs.length - 1 && (
              <span aria-hidden className="mx-2 h-px w-6 bg-[var(--border)]" />
            )}
          </li>
        ))}
      </ol>

      {next && (
        <p className="mt-3 border-t border-[var(--border)] pt-3 text-sm">
          <span className="text-muted">Next: </span>
          {next}
        </p>
      )}
    </section>
  )
}

/** One sentence, never a list — the ladder is climbed a rung at a time. */
function nextAction({
  counted,
  grouped,
  supplySet,
  measured,
  built,
}: {
  counted: number
  grouped: number
  supplySet: boolean
  measured: number
  built: BuiltPowerPlan
}): string | null {
  if (counted === 0) return 'add the gear this show runs, or seed the plan from a canvas.'
  if (built.coverage.unknown > 0)
    return `fill in ${built.coverage.unknown} ${built.coverage.unknown === 1 ? 'row that has' : 'rows that have'} no wattage — they are not in the total at all.`
  if (!supplySet) return 'set the supply — a PLN tier or a generator — to find out whether it fits.'
  if (grouped < counted)
    return `assign ${counted - grouped} ${counted - grouped === 1 ? 'row' : 'rows'} to a circuit to get per-circuit loads and phase balance.`
  if (measured < counted) return 'nothing outstanding. Measure the real draw on site to close the last rung.'
  return null
}
